/**
 * SchoolSupport — Tenant-side support ticket inbox.
 * Tenant admins view + reply to their own tickets; no cross-tenant data.
 * Props: tenantId
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LifeBuoy, Plus, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_COLORS = {
  open: 'bg-sky-100 text-sky-800',
  in_progress: 'bg-amber-100 text-amber-800',
  waiting_tenant: 'bg-fuchsia-100 text-fuchsia-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-slate-100 text-slate-600',
};

const PRIORITY_COLORS = {
  urgent: 'bg-rose-100 text-rose-800 border-rose-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  normal: 'bg-slate-100 text-slate-700 border-slate-300',
  low: 'bg-slate-100 text-slate-500 border-slate-300',
};

const BASE = (tid) => `/superadmin/tenants/${tid}/support`;

const SchoolSupport = ({ tenantId }) => {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ['tenant-support-tickets', tenantId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/tickets`)).data,
    refetchInterval: 60000,
  });

  return (
    <div className="space-y-5" data-testid="school-support-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-indigo-600" /> Support
          </h2>
          <p className="text-sm text-slate-500 mt-1">Submit requests and track responses from the platform team</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="tenant-support-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New Request
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3">Ticket #</th>
                <th className="text-left p-3">Subject</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Priority</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Messages</th>
                <th className="text-left p-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="p-5 text-center text-slate-500">Loading...</td></tr>}
              {!isLoading && tickets.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No support requests yet.</td></tr>}
              {tickets.map(t => (
                <tr key={t.id} onClick={() => setOpenId(t.id)} className="border-b cursor-pointer hover:bg-slate-50" data-testid={`tenant-support-row-${t.id}`}>
                  <td className="p-3 font-mono text-xs">{t.number}</td>
                  <td className="p-3 font-semibold">{t.subject}</td>
                  <td className="p-3 capitalize text-xs">{t.category?.replace('_', ' ')}</td>
                  <td className="p-3"><Badge variant="outline" className={`capitalize ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</Badge></td>
                  <td className="p-3"><Badge variant="outline" className={`capitalize ${STATUS_COLORS[t.status] || ''}`}>{t.status?.replace('_', ' ')}</Badge></td>
                  <td className="p-3 text-right font-mono text-slate-500">{t.message_count}</td>
                  <td className="p-3 text-xs text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <NewRequestDialog open={createOpen} onOpenChange={setCreateOpen} tenantId={tenantId}
                        onCreated={() => qc.invalidateQueries({ queryKey: ['tenant-support-tickets', tenantId] })} />
      {openId && <TicketThreadDialog tenantId={tenantId} ticketId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
};

const NewRequestDialog = ({ open, onOpenChange, tenantId, onCreated }) => {
  const [form, setForm] = useState({ subject: '', body: '', category: 'general', priority: 'normal' });
  const mut = useMutation({
    mutationFn: (data) => api.post(`${BASE(tenantId)}/tickets`, { ...data, tenant_id: tenantId }).then(r => r.data),
    onSuccess: (r) => { toast.success(`Request submitted: ${r.number}`); onOpenChange(false); onCreated?.(); setForm({ subject: '', body: '', category: 'general', priority: 'normal' }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Support Request</DialogTitle>
          <DialogDescription>Submit a question or issue to the platform team</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Subject *</Label>
            <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="tenant-support-subject" />
          </div>
          <div><Label>Message *</Label>
            <textarea className="w-full border rounded p-2 text-sm min-h-[100px]"
                      value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                      data-testid="tenant-support-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="tenant-support-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['general', 'billing', 'technical', 'feature_request', 'bug'].map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="tenant-support-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'normal', 'high', 'urgent'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => mut.mutate(form)}
                  disabled={mut.isPending || !form.subject || !form.body}
                  className="bg-indigo-600 text-white" data-testid="tenant-support-submit">
            {mut.isPending ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TicketThreadDialog = ({ tenantId, ticketId, onClose }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['tenant-ticket-detail', ticketId],
    queryFn: async () => (await api.get(`${BASE(tenantId)}/tickets/${ticketId}`)).data,
  });
  const [reply, setReply] = useState('');
  const replyMut = useMutation({
    mutationFn: () => api.post(`${BASE(tenantId)}/tickets/${ticketId}/respond`, { body: reply }),
    onSuccess: () => { toast.success('Reply sent'); setReply(''); qc.invalidateQueries({ queryKey: ['tenant-ticket-detail', ticketId] }); qc.invalidateQueries({ queryKey: ['tenant-support-tickets', tenantId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {isLoading ? <p>Loading...</p> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-slate-500">{data?.number}</span>
                <span>{data?.subject}</span>
                <Badge variant="outline" className={`capitalize ${STATUS_COLORS[data?.status]}`}>{data?.status?.replace('_', ' ')}</Badge>
                <Badge variant="outline" className={`capitalize ${PRIORITY_COLORS[data?.priority]}`}>{data?.priority}</Badge>
              </DialogTitle>
              <DialogDescription className="capitalize">{data?.category?.replace('_', ' ')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
              {(data?.thread || []).map((m, i) => {
                const fromPlatform = m.author_role === 'super_admin';
                return (
                  <div key={i} className={`p-3 rounded border text-sm ${fromPlatform ? 'bg-indigo-50 border-indigo-200 mr-8' : 'bg-slate-50 border-slate-200 ml-8'}`}>
                    <div className="flex items-center justify-between mb-1 text-xs">
                      <span className="font-semibold">
                        {fromPlatform ? '🛠 Platform Team' : m.author_email}
                      </span>
                      <span className="text-slate-500">{m.at ? new Date(m.at).toLocaleString() : ''}</span>
                    </div>
                    <p className="whitespace-pre-wrap">{m.body}</p>
                  </div>
                );
              })}
            </div>
            <div className="border-t pt-3">
              <Label>Your reply</Label>
              <textarea value={reply} onChange={e => setReply(e.target.value)}
                        placeholder="Type a reply..."
                        className="w-full border rounded p-2 text-sm min-h-[70px] mt-1"
                        data-testid="tenant-ticket-reply-body" />
              <Button onClick={() => replyMut.mutate()} disabled={!reply || replyMut.isPending}
                      className="bg-indigo-600 text-white mt-2" data-testid="tenant-ticket-reply-submit">
                <Send className="h-4 w-4 mr-1" /> {replyMut.isPending ? 'Sending...' : 'Send Reply'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SchoolSupport;
